import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FolderOpen, Upload, FileText, Search, Plus, Trash2, Download,
  MoreVertical, Brain, Calendar, Loader2, ChevronRight, Grid3X3,
  List, FolderPlus, ArrowRight, X, Share2, Eye
} from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';
import PageSkeleton from '../components/PageSkeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const EXT_COLORS = {
  pdf: 'text-red-400 bg-red-500/10',
  txt: 'text-blue-400 bg-blue-500/10',
  doc: 'text-indigo-400 bg-indigo-500/10',
  docx: 'text-indigo-400 bg-indigo-500/10',
  csv: 'text-green-400 bg-green-500/10',
  md: 'text-cyan-400 bg-cyan-500/10',
};

const DocumentUploadsPage = () => {
  const { isPremium } = useSubscription();
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  const [analyzing, setAnalyzing] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showMoveDialog, setShowMoveDialog] = useState(null);
  const [showStudyPlan, setShowStudyPlan] = useState(null);
  const [shareGroupDialog, setShareGroupDialog] = useState(null);
  const [myGroups, setMyGroups] = useState([]);
  const fileInputRef = useRef(null);
  const dropRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (activeFolder) params.folder = activeFolder;
      if (search) params.search = search;
      const [filesRes, foldersRes] = await Promise.all([
        axios.get(`${API}/uploads/files`, { params }),
        axios.get(`${API}/uploads/folders`),
      ]);
      setFiles(filesRes.data);
      setFolders(foldersRes.data.folders || []);
    } catch (err) {
      console.error('Failed to load files:', err);
    } finally {
      setLoading(false);
    }
  }, [activeFolder, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async (fileToUpload) => {
    if (!fileToUpload) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', fileToUpload);
      if (activeFolder) formData.append('folder', activeFolder);
      await axios.post(`${API}/uploads/upload?${activeFolder ? `folder=${encodeURIComponent(activeFolder)}` : ''}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / (e.total || 1))),
      });
      toast.success(`"${fileToUpload.name}" uploaded!`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleAnalyze = async (fileId) => {
    setAnalyzing(fileId);
    try {
      const res = await axios.post(`${API}/uploads/${fileId}/analyze`);
      toast.success('Document analyzed!');
      setShowStudyPlan(res.data);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(null);
    }
  };

  const handleDelete = async (fileId) => {
    try {
      // Backend route is DELETE /api/uploads/files/{file_id} (note the /files
      // segment) — earlier shape `/uploads/${fileId}` was 404'ing silently
      // which is why the disk-space reclamation never worked.
      await axios.delete(`${API}/uploads/files/${fileId}`);
      toast.success('File deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleMove = async (fileId, folder) => {
    try {
      await axios.put(`${API}/uploads/files/${fileId}/move?folder=${encodeURIComponent(folder)}`);
      toast.success(`Moved to ${folder}`);
      setShowMoveDialog(null);
      fetchData();
    } catch (err) {
      toast.error('Move failed');
    }
  };

  const handleShare = async (fileId, groupId) => {
    try {
      await axios.post(`${API}/collaboration/groups/${groupId}/resources?file_id=${fileId}`);
      toast.success('File shared to group!');
      setShareGroupDialog(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Share failed');
    }
  };

  /**
   * Download a file: fetch as blob (Bearer-auth headers travel with axios
   * defaults), then synthesize a hidden <a download> click. This avoids the
   * "anchor href download can't carry Authorization" foot-gun.
   */
  const handleDownload = async (fileId) => {
    const file = files.find((f) => f.id === fileId);
    const fallbackName = file?.original_filename || `download-${fileId}`;
    try {
      const res = await axios.get(`${API}/uploads/files/${fileId}/download`, {
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Release the blob URL on the next tick so the click has time to consume it.
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error('Download failed:', err);
      toast.error(err.response?.data?.detail || 'Download failed');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) handleUpload(droppedFile);
  };

  const totalSize = folders.reduce((sum, f) => sum + f.total_size, 0);
  const maxStorage = isPremium ? 10 * 1024 * 1024 * 1024 : 500 * 1024 * 1024;
  const storagePct = Math.min((totalSize / maxStorage) * 100, 100);

  return (
    <div className="max-w-6xl mx-auto space-y-6 py-4" data-testid="file-manager-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[var(--isp-heading)] font-heading">My Files</h1>
          <p className="text-sm text-[var(--isp-muted)]">
            {formatSize(totalSize)} of {formatSize(maxStorage)} used
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--isp-muted)]" />
            <Input
              placeholder="Search files..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-48 rounded-xl bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] text-[var(--isp-body)] text-sm"
              data-testid="file-search-input"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setViewMode(v => v === 'grid' ? 'list' : 'grid')}
            className="rounded-xl border-[var(--isp-glass-border)] text-[var(--isp-body)]"
            data-testid="view-mode-toggle"
          >
            {viewMode === 'grid' ? <List className="w-4 h-4" /> : <Grid3X3 className="w-4 h-4" />}
          </Button>
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="rounded-xl gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:opacity-90"
            data-testid="upload-file-button"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? `${uploadProgress}%` : 'Upload'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.txt,.doc,.docx,.csv,.md"
            className="hidden"
            onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ''; }}
          />
        </div>
      </div>

      {/* Storage bar */}
      <div className="h-1.5 rounded-full bg-[var(--isp-glass-border)] overflow-hidden">
        <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all" style={{ width: `${storagePct}%` }} />
      </div>

      <div className="flex gap-6">
        {/* Sidebar — Folders */}
        <div className="hidden sm:block w-48 flex-shrink-0 space-y-1" data-testid="folder-sidebar">
          <button
            onClick={() => setActiveFolder(null)}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
              !activeFolder ? 'bg-blue-500/10 text-blue-400 font-medium' : 'text-[var(--isp-body)] hover:bg-[var(--isp-glass-inner)]'
            }`}
            data-testid="folder-all"
          >
            <FolderOpen className="w-4 h-4" /> All Files
            <span className="ml-auto text-xs text-[var(--isp-muted)]">{files.length}</span>
          </button>
          {folders.map((f) => (
            <button
              key={f.name}
              onClick={() => setActiveFolder(f.name)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                activeFolder === f.name ? 'bg-blue-500/10 text-blue-400 font-medium' : 'text-[var(--isp-body)] hover:bg-[var(--isp-glass-inner)]'
              }`}
              data-testid={`folder-${f.name}`}
            >
              <FolderOpen className="w-4 h-4" />
              <span className="truncate">{f.name}</span>
              <span className="ml-auto text-xs text-[var(--isp-muted)]">{f.file_count}</span>
            </button>
          ))}
          <button
            onClick={() => setShowNewFolder(true)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[var(--isp-muted)] hover:text-[var(--isp-body)] hover:bg-[var(--isp-glass-inner)]"
            data-testid="new-folder-button"
          >
            <FolderPlus className="w-4 h-4" /> New Folder
          </button>
        </div>

        {/* Main area */}
        <div className="flex-1 min-w-0">
          {/* Drop zone */}
          <div
            ref={dropRef}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={` border-2 border-dashed transition-colors mb-4 ${
              dragOver ? 'border-blue-400 bg-blue-500/5' : 'border-transparent'
            }`}
          >
            {loading ? (
              <div data-testid="docs-loading">
                <PageSkeleton variant="grid" rows={6} />
              </div>
            ) : files.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-state">
                <FolderOpen className="w-12 h-12 text-[var(--isp-muted)] mb-3" />
                <h3 className="text-lg font-medium text-[var(--isp-heading)] font-heading">
                  {activeFolder ? `No files in "${activeFolder}"` : 'No files yet'}
                </h3>
                <p className="text-sm text-[var(--isp-muted)] mt-1 mb-4">
                  Drag & drop or click upload to add documents (PDF, TXT, DOC)
                </p>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded-xl gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white"
                  data-testid="empty-upload-button"
                >
                  <Upload className="w-4 h-4" /> Upload First File
                </Button>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <AnimatePresence>
                  {files.map((file) => (
                    <FileCard
                      key={file.id}
                      file={file}
                      analyzing={analyzing}
                      onAnalyze={handleAnalyze}
                      onDelete={handleDelete}
                      onDownload={handleDownload}
                      onMove={(id) => setShowMoveDialog(id)}
                      onShare={(id) => {
                        setShareGroupDialog(id);
                        axios.get(`${API}/collaboration/groups`).then(r => setMyGroups(r.data.groups || [])).catch(() => {});
                      }}
                      onViewPlan={(plan) => setShowStudyPlan(plan)}
                    />
                  ))}
                </AnimatePresence>
              </div>
            ) : (
              <div className="space-y-1">
                {files.map((file) => (
                  <FileRow
                    key={file.id}
                    file={file}
                    analyzing={analyzing}
                    onAnalyze={handleAnalyze}
                    onDelete={handleDelete}
                    onDownload={handleDownload}
                    onMove={(id) => setShowMoveDialog(id)}
                    onShare={(id) => {
                      setShareGroupDialog(id);
                      axios.get(`${API}/collaboration/groups`).then(r => setMyGroups(r.data.groups || [])).catch(() => {});
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Folder Dialog */}
      <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
        <DialogContent className="bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]" data-testid="new-folder-dialog">
          <DialogHeader><DialogTitle className="text-[var(--isp-heading)] font-heading">New Folder</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Folder name (e.g., Biology 101)"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              className="bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] text-[var(--isp-body)]"
              data-testid="new-folder-name"
            />
            <Button
              onClick={() => { if (newFolderName.trim()) { setActiveFolder(newFolderName.trim()); setShowNewFolder(false); setNewFolderName(''); toast.success(`Folder "${newFolderName}" created`); } }}
              className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white"
            >
              Create Folder
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move File Dialog */}
      <Dialog open={!!showMoveDialog} onOpenChange={() => setShowMoveDialog(null)}>
        <DialogContent className="bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]" data-testid="move-file-dialog">
          <DialogHeader><DialogTitle className="text-[var(--isp-heading)] font-heading">Move to Folder</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {folders.map((f) => (
              <button
                key={f.name}
                onClick={() => handleMove(showMoveDialog, f.name)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[var(--isp-body)] hover:bg-[var(--isp-glass-inner)] transition-colors"
              >
                <FolderOpen className="w-4 h-4" /> {f.name} <span className="ml-auto text-xs text-[var(--isp-muted)]">{f.file_count} files</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Share to Group Dialog */}
      <Dialog open={!!shareGroupDialog} onOpenChange={() => setShareGroupDialog(null)}>
        <DialogContent className="bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]" data-testid="share-group-dialog">
          <DialogHeader><DialogTitle className="text-[var(--isp-heading)] font-heading">Share to Study Group</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {myGroups.length === 0 ? (
              <p className="text-sm text-[var(--isp-muted)] text-center py-4">No study groups found. Join or create one first!</p>
            ) : myGroups.map((g) => (
              <button
                key={g.id}
                onClick={() => handleShare(shareGroupDialog, g.id)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[var(--isp-body)] hover:bg-[var(--isp-glass-inner)] transition-colors"
              >
                <Share2 className="w-4 h-4" /> {g.name}
                <span className="ml-auto text-xs text-[var(--isp-muted)]">{g.members?.length || 0} members</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Study Plan Viewer */}
      <Dialog open={!!showStudyPlan} onOpenChange={() => setShowStudyPlan(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]" data-testid="study-plan-dialog">
          <DialogHeader><DialogTitle className="text-[var(--isp-heading)] font-heading">AI Study Plan</DialogTitle></DialogHeader>
          {showStudyPlan && (
            <div className="space-y-4 text-sm text-[var(--isp-body)]">
              {showStudyPlan.summary && (
                <div>
                  <h4 className="font-bold text-[var(--isp-heading)] mb-1">Summary</h4>
                  <p>{showStudyPlan.summary}</p>
                </div>
              )}
              {showStudyPlan.topics?.length > 0 && (
                <div>
                  <h4 className="font-bold text-[var(--isp-heading)] mb-1">Key Topics</h4>
                  <ul className="list-disc ml-4 space-y-1">
                    {showStudyPlan.topics.map((t, i) => (
                      <li key={`topic-${i}`}>
                        {typeof t === 'string' ? t : (
                          <>
                            <span className="font-semibold text-[var(--isp-heading)]">{t.name || t.title || `Topic ${i + 1}`}</span>
                            {(t.description || t.detail) && (
                              <span className="text-[var(--isp-muted)]"> — {t.description || t.detail}</span>
                            )}
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {showStudyPlan.key_takeaways?.length > 0 && (
                <div>
                  <h4 className="font-bold text-[var(--isp-heading)] mb-1">Key Takeaways</h4>
                  <ul className="list-disc ml-4 space-y-0.5">
                    {showStudyPlan.key_takeaways.map((t, i) => (
                      <li key={`kt-${i}`}>{typeof t === 'string' ? t : (t.text || t.takeaway || JSON.stringify(t))}</li>
                    ))}
                  </ul>
                </div>
              )}
              {showStudyPlan.study_plan?.length > 0 && (
                <div>
                  <h4 className="font-bold text-[var(--isp-heading)] mb-1">Study Schedule</h4>
                  <ul className="list-disc ml-4 space-y-1">
                    {showStudyPlan.study_plan.map((step, i) => (
                      <li key={`step-${i}`}>
                        {typeof step === 'string' ? step : (
                          <>
                            {(step.day || step.week) && (
                              <span className="font-semibold text-[var(--isp-heading)]">{step.day ? `Day ${step.day}` : `Week ${step.week}`}{step.title ? ` — ${step.title}` : ''}: </span>
                            )}
                            <span>{step.task || step.activity || step.description || step.title || JSON.stringify(step)}</span>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {showStudyPlan.flashcards?.length > 0 && (
                <div>
                  <h4 className="font-bold text-[var(--isp-heading)] mb-1">Flashcards</h4>
                  <div className="space-y-2">
                    {showStudyPlan.flashcards.map((fc, i) => (
                      <Card key={`fc-${i}`} className="p-3 bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)]">
                        <p className="font-medium text-[var(--isp-heading)]">Q: {fc.question || fc.front || fc.q}</p>
                        <p className="mt-1 text-[var(--isp-muted)]">A: {fc.answer || fc.back || fc.a}</p>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
              {/* Fallback — show nothing rendered? At least surface the raw JSON so it's never empty */}
              {!showStudyPlan.summary && !showStudyPlan.topics?.length && !showStudyPlan.key_takeaways?.length && !showStudyPlan.study_plan?.length && !showStudyPlan.flashcards?.length && (
                <div className="text-[var(--isp-muted)] text-xs">
                  <p className="mb-2">No structured plan content available for this document yet. Try re-running the analyzer.</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// File Card (Grid view)
const FileCard = ({ file, analyzing, onAnalyze, onDelete, onDownload, onMove, onShare, onViewPlan }) => {
  const extColor = EXT_COLORS[file.extension] || 'text-gray-400 bg-gray-500/10';
  const [showMenu, setShowMenu] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
    >
      <Card
        className="p-4 rounded-xl bg-[var(--isp-glass)] border-[var(--isp-glass-border)] hover:border-blue-500/30 transition-all group"
        data-testid={`file-card-${file.id}`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className={`w-10 h-10 rounded-lg ${extColor} flex items-center justify-center text-xs font-bold uppercase`}>
            {file.extension}
          </div>
          <div className="relative">
            <button onClick={() => setShowMenu(!showMenu)} className="p-1 rounded hover:bg-[var(--isp-glass-inner)] text-[var(--isp-muted)]">
              <MoreVertical className="w-4 h-4" />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-7 z-10 w-40 rounded-lg bg-[var(--isp-glass)] border border-[var(--isp-glass-border)] shadow-lg py-1" onMouseLeave={() => setShowMenu(false)}>
                <button onClick={() => { onDownload(file.id); setShowMenu(false); }} className="w-full px-3 py-1.5 text-xs text-left text-[var(--isp-body)] hover:bg-[var(--isp-glass-inner)] flex items-center gap-2" data-testid={`download-file-menu-${file.id}`}>
                  <Download className="w-3 h-3" /> Download
                </button>
                <button onClick={() => { onMove(file.id); setShowMenu(false); }} className="w-full px-3 py-1.5 text-xs text-left text-[var(--isp-body)] hover:bg-[var(--isp-glass-inner)] flex items-center gap-2">
                  <ArrowRight className="w-3 h-3" /> Move
                </button>
                <button onClick={() => { onShare(file.id); setShowMenu(false); }} className="w-full px-3 py-1.5 text-xs text-left text-[var(--isp-body)] hover:bg-[var(--isp-glass-inner)] flex items-center gap-2">
                  <Share2 className="w-3 h-3" /> Share to Group
                </button>
                <button onClick={() => { onDelete(file.id); setShowMenu(false); }} className="w-full px-3 py-1.5 text-xs text-left text-red-400 hover:bg-red-500/10 flex items-center gap-2">
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            )}
          </div>
        </div>
        <h4 className="text-sm font-medium text-[var(--isp-heading)] truncate mb-1" title={file.original_filename}>
          {file.original_filename}
        </h4>
        <div className="flex items-center gap-2 text-xs text-[var(--isp-muted)] mb-3">
          <span>{formatSize(file.size)}</span>
          <span className="w-1 h-1 rounded-full bg-[var(--isp-muted)]" />
          <span>{file.folder}</span>
        </div>
        <div className="flex gap-1.5">
          {file.has_study_plan ? (
            <Button size="sm" variant="outline" className="flex-1 rounded-lg text-xs gap-1 border-[var(--isp-glass-border)] text-[var(--isp-body)]" onClick={() => onViewPlan(file.study_plan)} data-testid={`view-plan-${file.id}`}>
              <Eye className="w-3 h-3" /> View Plan
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="flex-1 rounded-lg text-xs gap-1 border-[var(--isp-glass-border)] text-[var(--isp-body)]"
              onClick={() => onAnalyze(file.id)}
              disabled={analyzing === file.id}
              data-testid={`analyze-${file.id}`}
            >
              {analyzing === file.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />}
              Analyze
            </Button>
          )}
        </div>
      </Card>
    </motion.div>
  );
};

// File Row (List view)
const FileRow = ({ file, analyzing, onAnalyze, onDelete, onDownload, onMove, onShare }) => {
  const extColor = EXT_COLORS[file.extension] || 'text-gray-400 bg-gray-500/10';

  return (
    <div
      className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[var(--isp-glass-inner)] transition-colors group"
      data-testid={`file-row-${file.id}`}
    >
      <div className={`w-8 h-8 rounded-lg ${extColor} flex items-center justify-center text-[10px] font-bold uppercase flex-shrink-0`}>
        {file.extension}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--isp-heading)] truncate">{file.original_filename}</p>
        <p className="text-xs text-[var(--isp-muted)]">{formatSize(file.size)} — {file.folder}</p>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button aria-label="Download" size="icon" variant="ghost" className="h-7 w-7 text-[var(--isp-muted)]" onClick={() => onDownload(file.id)} data-testid={`download-file-${file.id}`} title="Download">
          <Download className="w-3.5 h-3.5" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-[var(--isp-muted)]" onClick={() => onAnalyze(file.id)} disabled={analyzing === file.id} title="Analyze">
          {analyzing === file.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Brain className="w-3.5 h-3.5" />}
        </Button>
        <Button aria-label="Share" size="icon" variant="ghost" className="h-7 w-7 text-[var(--isp-muted)]" onClick={() => onShare(file.id)} title="Share to group">
          <Share2 className="w-3.5 h-3.5" />
        </Button>
        <Button aria-label="Next" size="icon" variant="ghost" className="h-7 w-7 text-[var(--isp-muted)]" onClick={() => onMove(file.id)} title="Move">
          <ArrowRight className="w-3.5 h-3.5" />
        </Button>
        <Button aria-label="Delete" size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => onDelete(file.id)} title="Delete">
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};

export default DocumentUploadsPage;
